import { Container, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from "@mui/x-data-grid-pro";
import makeStyles from "@mui/styles/makeStyles";
import { getQuotationsGridNavigationItem, getQuotationHiddenColumns,getQuotationColumns } from './utils';
import { formatDate, getLocalStorageItem, setLocalStorageItem } from '../../_helpers';
import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../_constants';
import { INITIAL_ROW_SELECTION_MODEL } from '../../components/DataGridV2/constants';
import { breadcrumbAction, quotationsAction, tabNavigationAction, } from '../../_actions';
import DataGridV2 from '../../components/DataGridV2';
import useGridDataModalHandler from '../../_hooks/useGridDataModalHandler';
import { useRef } from 'react';
import { getQuotations } from '../../apis/buyersGuideApis';


const QuotationList = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch()
  const app = useSelector((state) => state.app);
  const apiRef = useGridApiRef();
  const muiTableRef = useRef();
  const theme = useTheme()
  const keyPrefix = app === apps.huisinfo ? "BUYERS_GUIDE" : "GENERAL"
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);

  const {
    loading,
    loadedRows,
    sortModel,
    handleSort,
    handleCellSort,
    filterModel,
    handleFilter,
    handleSearch,
    searchText,
    getAllRecords,
    handleCellFilter,
    totalRecords,
    paginationModel,
    onPaginationModelChange,
    getAllExportRecords
  } = useGridDataModalHandler({
    additionalParams: { app },
    rowIdentifier: "id",
    apiCall: getQuotations,
    sortModal: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_SORT[keyPrefix], undefined
    ),
    filterModal: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_FILTER[keyPrefix], undefined
    ),
    paginationModel: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_PAGINATION[keyPrefix], undefined
    ),
    searchText: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_SEARCH_TEXT[keyPrefix], ""
    ),
    dateTimeFields: ["createdOn", "modifiedOn"]
  });

  useEffect(() => {
    const QuotationsGridNavigation = getQuotationsGridNavigationItem(t)
    dispatch(tabNavigationAction.setNavigationTab(QuotationsGridNavigation.tabs, 0))
    dispatch(breadcrumbAction.setBreadcrumbItem(QuotationsGridNavigation.breadCrumbs))
  }, [dispatch, t])

  useEffect(() => { return quotationsAction.clearQuotationsStore }, [])

  const columns = useMemo(() => {
    const columns = getQuotationColumns({
      t,
      i18n,
      classes,
      handleCellFilter,
      apiRef,
      handleCellSort,
      keyPrefix,
      theme
    });
    return [...columns , ...getQuotationHiddenColumns(t)];

  }, [t, i18n,theme, classes, handleCellFilter, apiRef, handleCellSort, keyPrefix]);

  const getQuotationsForPrintAndDownload = async ({ selectedColumnsNames = [], isAllSelected = true, idsToExport = [] }) => {
    const hiddenFields = getQuotationHiddenColumns(t)
    const allColumns = [...columns, ...hiddenFields];
    const columnNames = allColumns.reduce(
      (p, c) => ({ ...p, [c.field]: c.field }),
      {}
    );

    const {
      data: { results },
    } = await (selectedColumnsNames.length > 0 ? getAllExportRecords({
      exportColumns: selectedColumnsNames.concat(
        getQuotationHiddenColumns(t)
          .filter(c => c.download && !selectedColumnsNames.includes(c.field))
          .map(c => c.field)
      ),
      additionalCondition: isAllSelected ? [] : [{
        field: "id",
        operator: "isAnyOf",
        value: [
          ...(idsToExport)
        ],
      }]
    }) : getAllRecords());

    const data = results?.map((result) => {
      const row = Object.keys(columnNames).reduce((prev, key) => {
        const obj = { ...prev };
        obj[key] = result[key];
        return obj;
      }, {});

      if (row?.["createdOn"]) row["createdOn"] = formatDate(new Date(row["createdOn"]), true, "-", false);
      if (row?.["modifiedOn"]) row["modifiedOn"] = formatDate(new Date(row["modifiedOn"]), true, "-", false);
      if (row?.["closingDate"]) row["closingDate"] = formatDate(new Date(row["closingDate"]));
      if (row?.["quotationSignatureType"]?.toString()) row["quotationSignatureType"] = t(`db.domain.ondertekening.${row["quotationSignatureType"]}`);
      if (typeof row["isSigningStarted"] === "boolean") row["isSigningStarted"] = t(row["isSigningStarted"] ? "general.yes" : "general.no");
      return row;
    });
    return data;
  }

  const columnsForExport = (isAllDisplay = false) => {
    const hiddenFields = isAllDisplay ? [] : getQuotationHiddenColumns(t, true)
    return columns.filter(column => !hiddenFields.some(hiddenFields => hiddenFields.field === column.field))
  } 

     const initialState = useMemo(
       () => ({
         columns: {
           columnVisibilityModel: {
             ...(getLocalStorageItem(
               LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_VISIBILITY[keyPrefix], {})),
             ...getQuotationHiddenColumns (t,).reduce((acc, column) => {
               return { ...acc, [column.field]: false, };
             }, {}),
           },
         },
       }),
       [keyPrefix, t]
     ); 

  const onFilterModelChange = useCallback((newFilterMModel) => {
    handleFilter(newFilterMModel, (newFilterMModel) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_FILTER[keyPrefix],
        newFilterMModel
      );
    })
  }, [handleFilter, keyPrefix])

  const getToolbarProps = useMemo(() => {
    const toolbarProps = {
      apiRef: apiRef,
      columns: columns,
      title: t("buyersGuide.quotation"),
      totalRecords: totalRecords,
      getData: getQuotationsForPrintAndDownload,
      handleSearch: (value) => {
        handleSearch(value, (value) => {
          setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_SEARCH_TEXT[keyPrefix],
            value
          );
        });
      },
      searchText: searchText,
      selectedRows: rowSelectionModel,
    };
    return toolbarProps;
  }, [apiRef, columns, totalRecords, searchText, rowSelectionModel, handleSearch, keyPrefix]);

  return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <DataGridV2
        localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_SEARCH_TEXT[keyPrefix]}
        localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_ORDER[keyPrefix]}
        localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_WIDTH[keyPrefix]}
        localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_VISIBILITY[keyPrefix]}
        rowSelectionModel={rowSelectionModel}
        checkboxSelection
        columnsForExport={columnsForExport}
        columns={columns}
        ref={muiTableRef}
        apiRef={apiRef}
        loading={loading}
        toolbarProps={getToolbarProps}
        rowCount={totalRecords}
        rows={loadedRows}
        initialState={initialState}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={(paginationModal) =>
          onPaginationModelChange(paginationModal, (paginationModal) => {
            setLocalStorageItem(
              LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_PAGINATION[keyPrefix],
              paginationModal
            );
          })}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) =>
          handleSort(newSortModel, (newSortModal) => {
            setLocalStorageItem(
              LOCAL_STORAGE_GRID_KEYS.QUOTATIONS_COLUMN_SORT[keyPrefix],
              newSortModal
            );
          })
        }
        paginationMode="server"
        filterMode="server"
        onFilterModelChange={onFilterModelChange}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        {...(!!filterModel && { filterModel })}
      />
    </Container>
  )
}

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 0,

  },
}));
export default QuotationList
